import {
  Field,
  Image,
  ImageField,
  Item,
  LinkField,
  Text,
  getFieldValue,
  withDatasourceCheck,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';

import { ComponentProps } from 'lib/component-props';

import { RecordOfFields } from 'models/fields';

import classNames from 'classnames/bind';
import styles from './StackedIconCallout.module.scss';
import { ButtonVariant } from 'components/helpers/Button/Button';
import { IconVariant } from 'components/helpers/Icon';
import Link, { LinkVariant } from 'components/helpers/Link/Link';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';
import withStyles from 'lib/enhancers/withStyles';
import compose from 'lib/enhancers/compose';

const cx = classNames.bind(styles);

type StackedIconCalloutProps = ComponentProps & {
  fields: {
    icon: ImageField;
    title: Field<string>;
    titleHeadingLevel: Item | null;
    titleTextStyle: Item | null;
    description: Field<string>;
    link: LinkField;
    linkStyle: Item | null;
    linkIcon: Item | null;
    linkText: Field<string>;
  };
};

const StackedIconCallout = (props: StackedIconCalloutProps): JSX.Element => {
  const { className, fields } = props;
  const { sitecoreContext } = useSitecoreContext();
  const titleHeadingLevelItemField = fields.titleHeadingLevel?.fields as RecordOfFields;
  const titleHeadingLevelValue = getFieldValue<string>(titleHeadingLevelItemField, 'Value') || 'h2';

  const titleTextStyleItemField = fields.titleTextStyle?.fields as RecordOfFields;
  const titleTextStyleValue = getFieldValue<string>(titleTextStyleItemField, 'Value') || 'font-style-xl';

  const linkStyleField = fields.linkStyle?.fields as RecordOfFields;
  const linkStyleValue = getFieldValue<ButtonVariant | LinkVariant>(linkStyleField, 'Value');

  const linkIconField = fields.linkIcon?.fields as RecordOfFields;
  const linkIconValue = getFieldValue<IconVariant>(linkIconField, 'Value');

  return (
    <div className={cx('stackedIconCallout', className)}>
      {fields.icon.value?.src === undefined ? null : (
        <Image className={cx('stackedIconCallout__icon')} field={fields.icon} />
      )}

      {sitecoreContext.pageEditing || fields.title?.value.length ? (
        <Text
          className={cx('stackedIconCallout__title', titleTextStyleValue)}
          tag={titleHeadingLevelValue}
          field={fields.title}
          encode={sitecoreContext.pageEditing}
        />
      ) : null}

      {fields.description?.value.length ? (
        <RichTextWrapper className={cx('stackedIconCallout__description')} field={fields.description} tag="div" />
      ) : null}

      {fields.link?.value.href?.length && fields.linkText?.value.length ? (
        <Link
          className={cx('stackedIconCallout__link', 'link')}
          field={fields.link}
          icon={linkIconValue}
          variant={linkStyleValue}
          showLinkTextWithChildrenPresent={false}
        >
          <Text field={fields.linkText} encode={sitecoreContext.pageEditing} />
        </Link>
      ) : null}
    </div>
  );
};

export default compose<StackedIconCalloutProps>(withDatasourceCheck(), withStyles())(StackedIconCallout);
